/**
 * Licence — the signed record a managed deployment runs under. Revia signs
 * the payload with the Cloud KMS asymmetric key in its own project, an
 * ECDSA P-256 SHA-256 signature in DER form; the deploy robot writes the
 * resulting token to `platform/licence` in the client's project; the
 * client's checkout verifies it against the release's public key and
 * derives its state with `deriveLicenceState`.
 *
 * The token is compact and JWS-shaped:
 * `base64url(canonicalJSON(payload)) + "." + base64url(DER signature)`. The
 * signature covers the ASCII bytes of the first segment exactly, which
 * `licenceSigningInput` returns, so signer and verifier agree byte for byte.
 * The verifier lives at `@momo/types/licence-signature`, apart from this
 * file, because it needs `node:crypto` and the barrel loads in the browser.
 *
 * Dates are ISO strings, not Timestamps: the payload crosses projects through
 * the deploy robot and is signed as JSON.
 *
 * Fields justified:
 *   v          — the payload format; a verifier refuses a format it does not
 *                read.
 *   projectId  — the client's Google Cloud project id: binds the licence to
 *                the one project it was signed for.
 *   features   — the paid features the licence grants.
 *   endsAt     — the end date: active up to it, grace after it.
 *   graceDays  — the grace length, signed with the record so a change to the
 *                default never moves a licence already issued.
 * Excluded:
 *   signedAt   — the payload's meaning does not depend on when it was
 *                signed; Revia's own record keeps that.
 *   signature  — the token's second segment, never inside what it signs.
 *   deploymentId — the project id is the deployment's identity here.
 */
import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import { z } from "zod";
import type { Actor } from "./workspace.entity.ts";

// The paid features a licence can name. Revia's orchestrator names the
// values; none is named yet, so `features` is always `[]` and any entry is
// refused. When a gated feature is named this becomes a closed `z.enum` and
// `features` an array of it.
export type LicenceFeature = never;

// The grace length the signer writes into `graceDays` unless told otherwise.
// The fleet-distribution design defines the grace state but no length; 14
// days was set with the backoffice that signs licences.
export const LICENCE_GRACE_DAYS = 14;

// The verifying public key: the PEM export of the Cloud KMS signing key
// version in Revia's project (`gcloud kms keys versions get-public-key`).
// Version 1 of that key, exported on 2026-10-21; a rotation replaces the
// whole string with the next version's export.
export const LICENCE_PUBLIC_KEY_PEM = `-----BEGIN PUBLIC KEY-----
MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAEhgGR+u//blKy8UFU9lR5+Mu8JoUD
CTD86IEREwkLb+ODtOSyMSc8iN31Ld23iwV5M0an134LJYvHTLCis6yvtA==
-----END PUBLIC KEY-----`;

// A Google Cloud project id: 6–30 characters, lower-case letters, digits and
// hyphens, starting with a letter and not ending with a hyphen.
const ProjectIdSchema = z
	.string()
	.regex(
		/^[a-z][a-z0-9-]{4,28}[a-z0-9]$/,
		"expected a Google Cloud project id",
	);

export const LicencePayloadSchema = z
	.object({
		v: z.literal(1),
		projectId: ProjectIdSchema.describe(
			"the client's Google Cloud project id the licence is signed for",
		),
		features: z.tuple([]).describe("the paid features the licence grants"),
		endsAt: z.iso
			.datetime()
			.describe("the instant the licence ends and grace begins"),
		graceDays: z
			.number()
			.int()
			.min(0)
			.max(90)
			.describe("how many days after the end date the licence stays usable"),
	})
	.strict();
export type LicencePayload = z.infer<typeof LicencePayloadSchema>;

// `platform/licence` — the token Revia signed, written by the deploy robot
// through the Admin SDK. Checkout reads it to enforce; the admin reads it to
// show the state. The token carries nothing a member may not see: the
// payload above and a signature anyone holding the public key can check.
//
// Rules cannot ask "member of some organization" here: grants are keyed by
// organization id and this path names none. The `mfa` claim is the closest
// fact the token carries — verifyOTP mints it only for an address holding a
// grant or a listed founding domain, and a removed member loses it — so an
// anonymous scanner session or a bare sign-in reads nothing.
export const PlatformLicenceSchema = z
	.object({
		token: z.string().min(1).describe("the signed licence token"),
	})
	.strict();
export type PlatformLicence = z.infer<typeof PlatformLicenceSchema>;

export const PlatformLicence = entity<Actor>()({
	schema: PlatformLicenceSchema,
	actions: ["read"] as const,
})
	.pipe(({ can }) => [can("read").when((_data, me) => me.mfa.eq(true))])
	.adaptor(firestore({ hierarchicalGrants: true, path: "platform/licence" }));

function canonicalJson(value: unknown): string {
	if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
	if (value !== null && typeof value === "object") {
		const entries = Object.entries(value)
			.filter(([, entry]) => entry !== undefined)
			.sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
		return `{${entries.map(([key, entry]) => `${JSON.stringify(key)}:${canonicalJson(entry)}`).join(",")}}`;
	}
	return JSON.stringify(value);
}

function base64url(bytes: Uint8Array): string {
	let binary = "";
	for (const byte of bytes) binary += String.fromCharCode(byte);
	return btoa(binary)
		.replace(/\+/g, "-")
		.replace(/\//g, "_")
		.replace(/=+$/, "");
}

/** The token's first segment as ASCII bytes — exactly what the signature
 *  covers: base64url of the payload's JSON with sorted keys and no
 *  whitespace. */
export function licenceSigningInput(payload: LicencePayload): Uint8Array {
	const json = new TextEncoder().encode(
		canonicalJson(LicencePayloadSchema.parse(payload)),
	);
	return new TextEncoder().encode(base64url(json));
}

/** The token: the signing input, a dot, and the base64url DER signature
 *  the KMS key returned over it. */
export function composeLicenceToken(
	payload: LicencePayload,
	signatureDer: Uint8Array,
): string {
	return `${new TextDecoder().decode(licenceSigningInput(payload))}.${base64url(signatureDer)}`;
}

export type LicenceState = "active" | "grace" | "read-only";

const DAY_MS = 24 * 60 * 60 * 1000;

/** The deployment's licence state. `payload` is the verified payload, or
 *  null when the record is missing or its token does not verify;
 *  `projectId` is the project the deployment runs in, so a licence signed
 *  for another project is read-only. */
export function deriveLicenceState(
	payload: LicencePayload | null,
	now: Date,
	{
		licenceRequired,
		projectId,
	}: { licenceRequired: boolean; projectId: string },
): LicenceState {
	if (!licenceRequired) return "active";
	if (!payload || payload.projectId !== projectId) return "read-only";
	const endsAt = Date.parse(payload.endsAt);
	if (now.getTime() <= endsAt) return "active";
	if (now.getTime() <= endsAt + payload.graceDays * DAY_MS) return "grace";
	return "read-only";
}
